<template>
  <div class="switch-account-modal">
    <div v-if="otherAccounts.length" class="account-list x-scrollbar">
      <AccountItem
        v-for="acc in otherAccounts"
        :key="acc.id"
        :is-active="currentSelectedAccount?.id === acc.id"
        :account="{
          isAdmin: acc.permission !== USER_ADMIN.NORMAL,
          teamName: acc.org_name,
          accountName: acc.name,
          logoUrl: acc.logo_url,
          isIdentify: acc.is_identify,
          identify_info: acc.identify_info || {},
          rightsType: acc.rights_type,
          isBanned: !acc.is_active_user,
        }"
        @click="handleSwitch(acc)"
      />
    </div>
    <div class="switch-account-footer">
      <Button
        class="login-button"
        variant="purple"
        size="large"
        blank
        :disabled="!currentSelectedAccount"
        @click="handleLogin"
      >
        {{ $t('deng-lu-ci-zhang-hao') }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Account } from '@rendora/common/entities/user';
import { useUserStore } from '@rendora/common/store/user';
import { USER_ADMIN } from '@rendora/common/types/enums';
import { Button } from '@rendora/design/components/button/index';
import { computed, inject, onMounted, ref } from 'vue';
import { ModalInstance } from '../modals/factory';
import AccountItem from './AccountItem.vue';

const props = defineProps<{
  onLoginSuccess?: () => void;
}>();

const userStore = useUserStore();
const modalInstance = inject<ModalInstance>('modalInstance');
const currentSelectedAccount = ref<Account | null>(null);

onMounted(async () => {
  if (userStore.user && userStore.accountList.length === 0) {
    await userStore.fetchAccountList();
  }
  currentSelectedAccount.value = userStore.accountList[0];
});

const otherAccounts = computed<Account[]>(() => {
  const list = userStore.accountList || [];
  const currentId = userStore.user?.account_id;
  return list.filter(acc => acc.id !== currentId);
});

const handleSwitch = async (account: Account) => {
  currentSelectedAccount.value = account;
};

const handleLogin = async () => {
  if (!currentSelectedAccount.value) return;
  const shouldSkipReload = !!props.onLoginSuccess;

  await userStore.switchAccount(currentSelectedAccount.value, shouldSkipReload);

  if (!shouldSkipReload) {
    return;
  }

  modalInstance?.close();
  await props.onLoginSuccess?.();
};
</script>

<style scoped lang="scss">
@use '@rendora/design/styles/mixins.scss' as *;
.switch-account-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.account-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 228px;
  flex: 1;
  padding: 20px 6px 20px 20px;

  @include mobile {
    gap: responsive-size(8px);
    padding: responsive-size(20px) responsive-size(6px) responsive-size(20px) responsive-size(20px);
  }

  :deep(> div) {
    width: 100% !important;
  }
}

.switch-account-footer {
  box-shadow: var(--x-shadow);
  padding: 20px;

  @include mobile {
    padding: responsive-size(20px);
  }
}
</style>
